/* Copyright (c) Trimble Inc. All rights reserved. */

import { useEffect, useState, useCallback, useRef } from 'react';

import { TokenExchangeHelper } from '../../helpers/TokenExchanger.helper';
import { useTokenStore } from '../../stores/Token.store';
import { useUserStore } from '../../stores/User.store';
import { JWTParseHelper } from '../../helpers/JWTParser.helper';
import { TokenExchangerProps, TokenExchangerModel } from './TokenExchanger.model';
import { JwtToken, TokenExchangeResponse } from '../../models/Token.model';
import { Logger } from '@tcp-base/toolkit';
import { AuthCheckHelper } from '../../helpers/AuthChecker.helper';

/**
 * Hook for Token Exchange functionality
 *
 * Handles all business logic:
 * - Validates auth config is properly set with clientId, environment
 * - Processes OAuth callback only once when conditions are met
 * - Stores tokens and handles success/error scenarios
 * - Manages redirects and URL cleanup
 */
export const useTokenExchangeController = (
  props: TokenExchangerProps = {},
): TokenExchangerModel => {
  const { onSuccess, onError, redirectAfterSuccess } = props;

  // Get store functions
  const saveTokenDetails = useTokenStore((state) => state.saveTokenDetails);
  const saveUserData = useUserStore((state) => state.saveUserData);

  const [state, setState] = useState<{
    isProcessing: boolean;
    isComplete: boolean;
    error: string | null;
    tokens: unknown | null;
  }>({
    isProcessing: false,
    isComplete: false,
    error: null,
    tokens: null,
  });

  const hasProcessedRef = useRef(false); // Ensure processOAuthCallback runs only once

  /**
   * Process OAuth callback with all business logic
   */
  const processCallback = useCallback(async (): Promise<void> => {
    // Check if we should process (validates auth config, clientId, environment)
    if (!TokenExchangeHelper.shouldProcessOAuthCallback()) {
      return;
    }

    // Mark as processing to prevent duplicate calls
    hasProcessedRef.current = true;

    // Signal to SecureHttpClient (and any other observers) that an OAuth code
    // exchange is actively in progress.  We set this BEFORE the exchange so
    // that even after the `?code=` parameter is removed from the address
    // bar, clients can still detect the in-flight exchange and avoid
    // triggering a premature login redirect.
    try {
      sessionStorage.setItem('OAUTH_EXCHANGE_IN_PROGRESS', 'true');
    } catch {
      // sessionStorage not available — ignore
    }

    try {
      setState((prev) => ({ ...prev, isProcessing: true, error: null }));

      // Check for OAuth errors first (reads from URL — must happen before cleanOAuthCallbackUrl)
      const oauthError = TokenExchangeHelper.getOAuthCallbackError();
      if (oauthError) {
        const errorMsg = `OAuth authorization failed: ${oauthError}`;
        // Safe to clean URL now — we don't need the code for an error path
        TokenExchangeHelper.cleanOAuthCallbackUrl();
        try {
          sessionStorage.removeItem('OAUTH_EXCHANGE_IN_PROGRESS');
        } catch {
          // sessionStorage not available — ignore
        }
        setState({
          isProcessing: false,
          isComplete: false,
          error: errorMsg,
          tokens: null,
        });
        onError?.(new Error(oauthError));
        return;
      }

      // Process the OAuth callback — reads ?code= from the URL.
      // URL is NOT cleaned inside the helper; we clean it below only after the
      // token is safely stored.
      const result: TokenExchangeResponse = await TokenExchangeHelper.processOAuthCallback();

      if (result.success && result.tokens) {
        // Success - save tokens and user data using new store-based approach
        await saveTokenAndUserData(result.tokens);

        // Token is now stored — clean OAuth params from the address bar after a short
        // delay so all in-flight reads of ?code= (isOAuthCallback checks etc.) still
        // see the callback URL during the current tick.
        setTimeout(() => {
          TokenExchangeHelper.cleanOAuthCallbackUrl();
        }, 1000);

        // Token is now in Zustand — safe to clear the in-progress flags.
        try {
          sessionStorage.removeItem('OAUTH_EXCHANGE_IN_PROGRESS');
          sessionStorage.removeItem('AUTH_REFRESHING');
        } catch {
          // sessionStorage not available — ignore
        }

        setState({
          isProcessing: false,
          isComplete: true,
          error: null,
          tokens: result.tokens,
        });

        // Call success callback
        onSuccess?.(result.tokens);

        AuthCheckHelper.handlePostLoginRedirect();
      } else {
        // Error during token exchange — clean URL and report error.
        TokenExchangeHelper.cleanOAuthCallbackUrl();
        const errorMessage = result.error || 'Unknown error during token exchange';
        try {
          sessionStorage.removeItem('OAUTH_EXCHANGE_IN_PROGRESS');
        } catch {
          // sessionStorage not available — ignore
        }
        setState({
          isProcessing: false,
          isComplete: false,
          error: errorMessage,
          tokens: null,
        });

        onError?.(new Error(errorMessage));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unexpected error during token exchange';

      // Best-effort URL cleanup in the unexpected-error path.
      TokenExchangeHelper.cleanOAuthCallbackUrl();
      try {
        sessionStorage.removeItem('OAUTH_EXCHANGE_IN_PROGRESS');
      } catch {
        // sessionStorage not available — ignore
      }
      setState({
        isProcessing: false,
        isComplete: false,
        error: errorMessage,
        tokens: null,
      });

      Logger.error('useTokenExchange: Unexpected error:', error);
      onError?.(new Error(errorMessage));
    }
  }, [onSuccess, onError, redirectAfterSuccess, saveTokenDetails, saveUserData]);

  const saveTokenAndUserData = useCallback(
    async (tokens: JwtToken) => {
      try {
        // Calculate expiry time from expires_in (seconds) + current time
        const expiryTime = Date.now() + tokens.expires_in * 1000;

        // Save token details to TokenStore (and IndexedDB)
        await saveTokenDetails({
          access_token: tokens.access_token,
          id_token: tokens.id_token,
          expiry_time: expiryTime,
        });

        // Extract and save user data from ID token if available
        if (tokens.id_token) {
          try {
            const userData = JWTParseHelper.extractUserFromJWT(tokens.id_token);
            if (userData.email || userData.given_name) {
              // Filter out default profile pictures - set to undefined if URL contains /default_profile.png
              const filteredPicture =
                userData.picture && userData.picture.includes('/default_profile.png')
                  ? undefined
                  : userData.picture;

              await saveUserData({
                email: userData.email,
                given_name: userData.given_name,
                family_name: userData.family_name,
                account_id: userData.account_id,
                picture: filteredPicture,
                user_id: userData.sub,
              });
            }
          } catch (jwtError) {
            Logger.warn('useTokenExchange: Failed to extract user data from ID token:', jwtError);
            // Don't fail the whole process if user extraction fails
          }
        }
      } catch (saveError) {
        Logger.error('useTokenExchange: Failed to save data via stores:', saveError);
        // Continue with success flow even if save fails
      }
    },
    [saveTokenDetails, saveUserData],
  );
  /**
   * Retry function for manual retry
   */
  const retryExchange = useCallback(() => {
    hasProcessedRef.current = false; // Reset the guard
    processCallback();
  }, [processCallback]);

  /**
   * Effect to trigger OAuth callback processing
   * Only runs when auth config is properly set
   */
  useEffect(() => {
    // Only process if we have a valid OAuth callback and haven't processed yet
    if (TokenExchangeHelper.isValidOAuthCallback() && !hasProcessedRef.current) {
      processCallback();
    }
  }, [processCallback]);

  return {
    ...state,
    retryExchange,
  };
};

export default useTokenExchangeController;
